import React, { type ChangeEvent, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question, SelectOptionDefault, SelectOptionGroup } from '../../../config/types';
import { isObject } from '../../../utils/array-utils';
import {
  getConditionalQuestionDefaultValues,
  getConditionalQuestionNames,
} from '../../../utils/config-utils';
import { validateField } from '../../../utils/form-utils';
import {
  getSingleSelectableOptionValue,
  resolveSelectOptions,
} from '../../../utils/select-assets';
import DCFMarkdown from '../../DCFMarkdown/DCFMarkdown';
import AutocompleteWrapper from '../Autocomplete/AutocompleteWrapper';
import Checkbox from '../Checkbox';
import { DateInputField } from '../DateInputField/DateInputField';
import { ShortDateInputField } from '../DateInputField/ShortDateInputField';
import Details from '../Details';
import ErrorMessage from '../ErrorMessage';
import OptionalLabel from '../OptionalLabel';
import ConditionalRadioField from '../RadioGroup/ConditionalRadioField';
import SelectInputField from '../SelectInputField';
import TextInputField from '../TextInputField';
import Textarea from '../Textarea';
import ConditionalCheckboxField from './ConditionalCheckboxField';
import { DefaultCheckbox } from './DefaultCheckbox';
import type {
  CheckboxGroupProps,
  CheckboxItemProps,
  CheckboxSubGroupProps,
  ConditionalCheckboxProps,
} from './Props';

const ConditionalCheckbox = ({
  id,
  anchorId,
  index,
  item,
  currentValue,
  name,
  disabled,
  showRequiredFlag,
  assets,
  handleOnChange,
}: ConditionalCheckboxProps) => {
  const value = item.option;
  const fieldId = !!anchorId && index === 0 ? anchorId : `${id}${value}${anchorId}`;
  const questions = (item.questions as Question[]) ?? [];
  const showConditional = !!questions.length && currentValue.includes(value);
  const conditionalFieldId = `${fieldId}_conditional`;

  const {
    setValue,
    unregister,
    watch,
    formState: { errors },
  } = useFormContext();
  const allValues = watch();

  const previousValue = useRef<string[]>();

  useEffect(() => {
    const wasSelected = previousValue.current?.includes(value);
    const isSelected = currentValue.includes(value);
    if (wasSelected && !isSelected) {
      const resetValues = getConditionalQuestionDefaultValues(questions);
      Object.entries(resetValues).forEach(([questionName, resetValue]) => {
        setValue(questionName, resetValue, { shouldDirty: true, shouldValidate: false });
      });
      unregister(getConditionalQuestionNames(questions), {
        keepDefaultValue: true,
        keepValue: true,
      });
    }
    previousValue.current = currentValue;
  }, [currentValue, questions, setValue, unregister, value]);

  return (
    <>
      <div className="govuk-checkboxes__item" key={fieldId}>
        <Checkbox
          id={fieldId}
          name={name}
          disabled={disabled ?? false}
          value={value}
          checked={currentValue.includes(value)}
          onChange={(e) => handleOnChange(e, value)}
          aria-controls={conditionalFieldId}
          aria-expanded={showConditional}
        />
        <label className="govuk-label govuk-checkboxes__label" htmlFor={fieldId}>
          {value}
        </label>
      </div>
      {showConditional && (
        <div
          className={
            showConditional
              ? 'govuk-checkboxes__conditional'
              : 'govuk-checkboxes__conditional govuk-checkboxes__conditional--hidden'
          }
          id={conditionalFieldId}
          hidden={!showConditional}
        >
          {questions.map((question) => {
            const rules = question?.rules || {};
            const questionLabelClassName = `govuk-label ${
              showRequiredFlag === true && rules?.required === true
                ? 'required-question-label'
                : ''
            }`;
            const questionLabel = (
              <OptionalLabel text={question.title} optional={rules?.required !== true} />
            );
            const shouldRenderFieldLabel =
              question.questionType !== 'radio' && question.questionType !== 'checkbox';
            const hideSingleOptionChildSelect =
              question.questionType === 'select' &&
              getSingleSelectableOptionValue(
                resolveSelectOptions({
                  options: question.options,
                  questionName: question.name,
                  assets,
                  answers: allValues,
                }),
              ) !== undefined;

            if (question.questionType === 'select' && hideSingleOptionChildSelect) {
              return (
                <SelectInputField
                  key={question.name}
                  name={question.name}
                  id={`${id}${question.name}`}
                  hintOption={question.hintOption}
                  options={question.options || []}
                  assets={assets}
                  disabled={disabled}
                  rules={rules}
                  title={question.title}
                  errorMessages={question.errorMessages}
                  detailsHeading={question.detailsHeading}
                  detailsContent={question.detailsContent}
                  defaultValue={
                    typeof question.defaultValue === 'string' ? question.defaultValue : undefined
                  }
                  autoFillSingleOptionAndHide
                />
              );
            }

            return (
              <div
                className={`govuk-form-group ${
                  errors?.[question.name] && 'govuk-form-group--error'
                }`}
                key={question.name}
              >
                {shouldRenderFieldLabel ? (
                  <label className={questionLabelClassName} htmlFor={`${id}${question.name}`}>
                    {questionLabel}
                  </label>
                ) : (
                  <div className={questionLabelClassName}>{questionLabel}</div>
                )}
                {question.hint && <DCFMarkdown content={question.hint} type="hint-text" />}
                {question.questionType === 'textInput' && (
                    <TextInputField
                      name={question.name}
                      approver={question.approver}
                      allowSelfApprover={question.allowSelfApprover}
                      restrictDomains={question.restrictDomains}
                      readOnly={question.readOnly}
                      disabled={disabled ?? false}
                      id={`${id}${question.name}`}
                      rules={rules}
                    title={question.title}
                    placeholder={question.placeholder}
                    wordCount={question.wordCount}
                    characterCount={question.characterCount}
                    errorMessages={question.errorMessages}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                  />
                )}
                {question.questionType === 'textarea' && (
                  <Textarea
                    name={question.name}
                    disabled={disabled ?? false}
                    numeric={!!question.rules?.number || undefined}
                    id={`${id}${question.name}`}
                    rules={rules}
                    title={question.title}
                    placeholder={question.placeholder}
                    wordCount={question.wordCount}
                    characterCount={question.characterCount}
                    textRows={question.textRows}
                    errorMessages={question.errorMessages}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                  />
                )}
                {question.questionType === 'radio' && (
                  <ConditionalRadioField
                    question={question}
                    disabled={disabled ?? false}
                    id={`${id}${question.name}`}
                    anchorId={`${id}${question.name}`}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                    assets={assets}
                  />
                )}
                {question.questionType === 'date' && (
                  <DateInputField
                    question={question}
                    anchorId={`${id}${question.name}`}
                    disabled={disabled}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                  />
                )}
                {question.questionType === 'shortDate' && (
                  <ShortDateInputField
                    question={question}
                    anchorId={`${id}${question.name}`}
                    disabled={disabled}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                  />
                )}
                {question.questionType === 'checkbox' && (
                  <ConditionalCheckboxField
                    question={question}
                    disabled={disabled ?? false}
                    id={`${id}${question.name}`}
                    anchorId={`${id}${question.name}`}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                    assets={assets}
                  />
                )}
                {question.questionType === 'select' && (
                  <SelectInputField
                    name={question.name}
                    id={`${id}${question.name}`}
                    hintOption={question.hintOption}
                    options={question.options || []}
                    assets={assets}
                    disabled={disabled}
                    rules={rules}
                    title={question.title}
                    errorMessages={question.errorMessages}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                    defaultValue={
                      typeof question.defaultValue === 'string' ? question.defaultValue : undefined
                    }
                    showRequiredFlag={showRequiredFlag}
                  />
                )}
                {question.questionType === 'autoComplete' && (
                  <AutocompleteWrapper
                    id={id}
                    items={question.items || []}
                    options={Array.isArray(question.options) ? question.options : []}
                    name={question.name}
                    title={question.title}
                    rules={rules}
                    matchLimit={question.matchLimit}
                    fuzzyMatch={question.fuzzyMatch}
                    placeholder={question.searchPlaceholder || question.placeholder}
                    detailsHeading={question.detailsHeading}
                    detailsContent={question.detailsContent}
                    showRequiredFlag={showRequiredFlag}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

const CheckboxItem = ({
  id,
  anchorId,
  index,
  item,
  currentValue,
  name,
  disabled,
  showRequiredFlag,
  assets,
  handleOnChange,
}: CheckboxItemProps) => {
  const isConditionalCheckbox = isObject(item) && 'questions' in item;
  return (
    <>
      {isConditionalCheckbox && (
        <ConditionalCheckbox
          id={id}
          anchorId={anchorId}
          name={name}
          disabled={disabled}
          showRequiredFlag={showRequiredFlag}
          index={index}
          item={item as SelectOptionDefault}
          currentValue={currentValue}
          assets={assets}
          handleOnChange={handleOnChange}
        />
      )}
      {!isConditionalCheckbox && (
        <DefaultCheckbox
          id={id}
          name={name}
          disabled={disabled}
          anchorId={anchorId}
          index={index}
          item={item as string}
          currentValue={currentValue}
          handleOnChange={handleOnChange}
        />
      )}
    </>
  );
};

const CheckboxSubGroup = ({
  id,
  anchorId,
  currentValue,
  option,
  name,
  disabled,
  showRequiredFlag,
  assets,
  handleOnChange,
}: CheckboxSubGroupProps) => (
  <div className="govuk-radios fieldset-container" data-module="govuk-radios" key={option.label}>
    <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
      <h3 className="govuk-fieldset__heading">{option.label}</h3>
    </legend>
    {option.options.map((option, index) => (
      <CheckboxItem
        id={id}
        name={name}
        disabled={disabled}
        showRequiredFlag={showRequiredFlag}
        key={option}
        anchorId={anchorId}
        index={index}
        item={option as string | SelectOptionDefault}
        currentValue={currentValue}
        assets={assets}
        handleOnChange={handleOnChange}
      />
    ))}
  </div>
);

export default function CheckboxGroup({
  anchorId,
  question,
  disabled,
  id,
  showRequiredFlag,
  detailsHeading,
  detailsContent,
  assets,
}: CheckboxGroupProps) {
  const { options, name, title, rules, errorMessages } = question;
  const resolvedOptions = Array.isArray(options) ? options : [];
  const { register, getFieldState, getValues, setValue, watch } = useFormContext();
  const defaultValue = Array.isArray(question.defaultValue)
    ? question.defaultValue
    : question.defaultValue
      ? [question.defaultValue]
      : [];
  const watchedValue = watch(name, defaultValue);
  const currentValue = Array.isArray(watchedValue) ? watchedValue : defaultValue;

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>, option: string) => {
    const newValues = e.target.checked
      ? [...currentValue, option]
      : currentValue.filter((v: string) => v !== option);

    setValue(name, newValues, { shouldDirty: true, shouldValidate: true });
  };

  useEffect(() => {
    if (getValues(name) === undefined) {
      setValue(name, defaultValue);
    }
  }, [defaultValue, getValues, name, setValue]);

  const hooksFormRegister = register(name, {
    validate: (value: string) => validateField(title, rules || {}, value, errorMessages),
  });
  const { error } = getFieldState(name);

  const optionIsSubGroup: boolean =
    (resolvedOptions[0] && typeof resolvedOptions[0] === 'object' && 'label' in resolvedOptions[0]) ||
    false;

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div className="govuk-checkboxes" data-module="govuk-checkboxes">
        {resolvedOptions.map((option, i) => (
          <React.Fragment key={i}>
            {optionIsSubGroup && (
              <CheckboxSubGroup
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                showRequiredFlag={showRequiredFlag}
                anchorId={anchorId}
                option={option as SelectOptionGroup}
                currentValue={currentValue}
                assets={assets}
                handleOnChange={handleOnChange}
              />
            )}
            {!optionIsSubGroup && (
              <CheckboxItem
                {...hooksFormRegister}
                id={id}
                name={name}
                disabled={disabled}
                showRequiredFlag={showRequiredFlag}
                anchorId={anchorId}
                index={i}
                item={option as string | SelectOptionDefault}
                currentValue={currentValue}
                assets={assets}
                handleOnChange={handleOnChange}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}
